import React from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Card, { CardTypes } from "../Card";
import FeatureList from "../FeatureList";
import { Button } from "@mui/material";
import { RoomCategories } from "../../configs";
import { Link } from "react-router-dom";
import LazyImage from "../LazyImage";

interface RoomDetailsProps {
  categoryId: string;
}

function RoomDetails({ categoryId }: RoomDetailsProps) {
  const room = RoomCategories.find((cat) => cat.id === categoryId);

  if (!room) {
    return null;
  }

  return (
    <Card type={CardTypes.Primary}>
      <div className="flex flex-col gap-6">
        {/* Top Row: Carousel and Description */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Room Gallery - Left */}
          <div className="flex lg:w-1/2 w-full justify-center bg-secondary-main rounded-lg overflow-hidden h-[280px] lg:h-[350px] shadow-inner">
            <Carousel
              key={categoryId}
              dynamicHeight={false}
              infiniteLoop={true}
              showThumbs={false}
              showStatus={false}
              showIndicators={true}
              className="w-full h-full"
            >
              {room.gallery.map((image, index) => (
                <div
                  key={index}
                  className="h-[280px] lg:h-[350px] flex items-center justify-center bg-secondary-main"
                >
                  <LazyImage
                    src={image.original}
                    alt={`${room.name} ${index + 1}`}
                    className="max-h-full max-w-full object-contain"
                    containerClassName="h-full w-full"
                  />
                </div>
              ))}
            </Carousel>
          </div>

          {/* Room Description - Right */}
          <div className="flex lg:w-1/2 flex-col justify-center">
            <div className="space-y-4">
              {/* Header Section with Pricing */}
              <div>
                <div className="flex justify-between items-start gap-4 mb-3">
                  <div className="flex-1">
                    <h1 className="text-2xl lg:text-3xl font-bold text-primary-main leading-tight">
                      {room.name}
                    </h1>
                    <p className="text-base text-primary-main mt-2 leading-relaxed">
                      {room.shortDescription}
                    </p>
                  </div>
                  {room.pricing && room.pricing.basePrice !== undefined && (
                    <div className="bg-primary-main rounded-lg px-4 py-3 shadow-lg border border-primary-main border-opacity-20">
                      <p className="text-xl font-bold text-secondary-main">
                        {room.pricing.currency}{" "}
                        {room.pricing.basePrice.toLocaleString()}
                      </p>
                      <p className="text-sm text-secondary-main opacity-90">
                        {room.pricing.perNight ? "per night" : ""}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Room Description */}
              <div className="bg-primary-main bg-opacity-10 rounded-lg p-4 border-l-4 border-primary-main">
                <p className="text-sm text-primary-main leading-relaxed">
                  {room.description}
                </p>
              </div>

              {/* CTA Button */}
              <div className="flex pt-2 w-full justify-center lg:justify-start">
                <Button
                  className="!bg-secondary-dark hover:!bg-secondary-medium !text-primary-main hover:!text-primary-main !font-semibold !rounded-lg !py-3 !px-6 !text-base !shadow-lg hover:!shadow-xl !transition-all !duration-300 w-2/3 lg:w-1/2"
                  variant="contained"
                  href={`/reserve?room=${room.id}`}
                >
                  Reserve {room.name}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Room Features - Full Width */}
        <div className="w-full">
          <div className="bg-secondary-main bg-opacity-10 rounded-lg px-6">
            {/* <h3 className="text-md font-semibold mb-4 text-primary-main text-left">
              Room Features
            </h3> */}
            <div className="flex justify-center">
              <FeatureList features={room.featureList} showSeeMore={false} />
            </div>
          </div>

          {/* Facilities Link */}
          <div className="text-center mt-4">
            <Link
              to="/facilities"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-main hover:text-secondary-dark transition-colors duration-200"
            >
              View all property facilities & amenities
              <span className="text-lg">→</span>
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default RoomDetails;
